import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Eye, EyeOff, Upload, TestTube, Shield, Loader2 } from 'lucide-react';
import { DataSourceType } from './DataSourceConnector';
import { useToast } from '@/hooks/use-toast';

interface ConnectionFormProps {
  dataSourceType: DataSourceType;
  onSubmit: (config: Record<string, any>) => Promise<void>;
  isSubmitting?: boolean;
  initialValues?: Record<string, any>;
}

const createValidationSchema = (fields: DataSourceType['configFields']) => {
  const schemaFields: Record<string, z.ZodTypeAny> = {
    name: z.string().min(1, 'Connection name is required').max(50, 'Name too long'),
    testOnSave: z.boolean().default(true),
  };

  fields.forEach(field => {
    let fieldSchema: z.ZodTypeAny;

    switch (field.type) {
      case 'number':
        fieldSchema = z.coerce.number();
        break;
      case 'url':
        fieldSchema = z.string().url('Invalid URL format');
        break;
      case 'file':
        fieldSchema = z.any(); // File validation handled separately
        break;
      default:
        fieldSchema = z.string();
    }

    if (field.required) {
      fieldSchema = fieldSchema.refine(val => val !== '' && val != null, {
        message: `${field.label} is required`
      });
    } else {
      fieldSchema = fieldSchema.optional();
    }

    schemaFields[field.name] = fieldSchema;
  });

  return z.object(schemaFields);
};

export const ConnectionForm: React.FC<ConnectionFormProps> = ({
  dataSourceType,
  onSubmit,
  isSubmitting = false,
  initialValues = {}
}) => {
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const { toast } = useToast();

  const validationSchema = createValidationSchema(dataSourceType.configFields);
  type FormData = z.infer<typeof validationSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: initialValues.name || `${dataSourceType.name} Connection`,
      testOnSave: initialValues.testOnSave ?? true,
      ...initialValues,
    }
  });

  const togglePasswordVisibility = (fieldName: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const handleFileUpload = (fieldName: string, file: File | null) => {
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));
      form.setValue(fieldName, file.name);
    } else {
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[fieldName];
        return newFiles;
      });
      form.setValue(fieldName, '');
    }
  };

  const handleSubmit = async (data: FormData) => {
    try {
      // Include uploaded files in the submission
      const configWithFiles = {
        ...data,
        ...Object.keys(uploadedFiles).reduce((acc, fieldName) => {
          acc[fieldName] = uploadedFiles[fieldName];
          return acc;
        }, {} as Record<string, File>)
      };

      await onSubmit(configWithFiles);
    } catch (error) {
      toast({
        title: "Configuration Error",
        description: error instanceof Error ? error.message : "Failed to save configuration",
        variant: "destructive"
      });
    }
  };

  const renderField = (field: DataSourceType['configFields'][0]) => {
    switch (field.type) {
      case 'password':
        return (
          <FormField
            key={field.name}
            control={form.control}
            name={field.name}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...formField}
                      type={showPasswords[field.name] ? 'text' : 'password'}
                      placeholder={field.placeholder}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => togglePasswordVisibility(field.name)}
                    >
                      {showPasswords[field.name] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                {field.helpText && (
                  <FormDescription>{field.helpText}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'file':
        return (
          <FormField
            key={field.name}
            control={form.control}
            name={field.name}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </FormLabel>
                <FormControl>
                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept=".csv,.json,.xml,.xlsx"
                      onChange={(e) => handleFileUpload(field.name, e.target.files?.[0] || null)}
                      className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {uploadedFiles[field.name] && (
                      <div className="text-sm text-green-600 flex items-center gap-2">
                        <TestTube className="h-4 w-4" />
                        Selected: {uploadedFiles[field.name].name}
                      </div>
                    )}
                  </div>
                </FormControl>
                {field.helpText && (
                  <FormDescription>{field.helpText}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'number':
        return (
          <FormField
            key={field.name}
            control={form.control}
            name={field.name}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...formField}
                    type="number"
                    placeholder={field.placeholder}
                  />
                </FormControl>
                {field.helpText && (
                  <FormDescription>{field.helpText}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return (
          <FormField
            key={field.name}
            control={form.control}
            name={field.name}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...formField}
                    type={field.type}
                    placeholder={field.placeholder}
                  />
                </FormControl>
                {field.helpText && (
                  <FormDescription>{field.helpText}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Configure {dataSourceType.name} Connection</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Connection Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Connection Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter a name for this connection" />
                  </FormControl>
                  <FormDescription>
                    A descriptive name to identify this data source
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Dynamic configuration fields */}
            {dataSourceType.configFields.map(renderField)}

            {/* Test on Save option */}
            <FormField
              control={form.control}
              name="testOnSave"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Test Connection</FormLabel>
                    <FormDescription>
                      Automatically test the connection when saving
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {form.watch('testOnSave') ? 'Testing Connection...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <TestTube className="mr-2 h-4 w-4" />
                    {form.watch('testOnSave') ? 'Save & Test Connection' : 'Save Configuration'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};